import axios from 'axios'
import store from '@/store'
import router from '@/router'

const service = axios.create({
    baseURL: 'https://api.scszcb.com/', // url = base url + request url
    timeout: 5000, // request timeout
    headers: {
        'Content-Type': 'application/json'
    }
})

// 标记是否正在处理401错误，避免重复跳转
let isHandling401 = false

// 请求拦截器：统一添加token到请求头
service.interceptors.request.use(
    config => {
        try {
            // 定义不需要token的接口列表（支持有/没有前导斜杠的URL）
            const noAuthUrls = [
                'dtb/v1/user/gw_login',      // 登录接口
                'dtb/v1/user/gw_register',   // 注册接口（如果有）
                'api/banner/list',           // 公开接口（banner）
                'api/honor/list',            // 公开接口（荣誉）
                'api/new/list',              // 公开接口（新闻列表）
                'api/new',                   // 公开接口（新闻详情）
                'api/org/list',              // 公开接口（机构列表）
                'api/biz/list',              // 公开接口（业务列表）
                'api/biz/primary',           // 公开接口（一级分类）
                'api/publish/detail',        // 公开接口（出版详情）
                'api/publish/second',        // 公开接口
                'api/publish/primary',       // 公开接口
                'api/music/detail',          // 公开接口（音乐详情）
                'api/music/second',          // 公开接口
                'api/music/primary',         // 公开接口
                'api/blockchain/second',     // 公开接口
                'api/blockchain/primary',    // 公开接口
                'api/game/second',           // 公开接口
                'api/game/primary',          // 公开接口
                'api/introduction/list',     // 公开接口（关于我们）
                'api/certificate/list',      // 公开接口（荣誉展示）
                'api/new/previous',          // 公开接口（上一篇）
                'api/new/next',              // 公开接口（下一篇）
            ]
            
            // 检查当前请求URL是否需要token
            // 统一处理URL格式（移除前导斜杠，移除查询参数）
            let url = (config.url || '').replace(/^\/+/, '') // 移除前导斜杠
            const urlPath = url.split('?')[0] // 移除查询参数，只保留路径部分
            
            // 检查URL是否在不需要token的列表中
            const needsAuth = !noAuthUrls.some(noAuthUrl => {
                // 检查路径是否匹配（支持完整匹配或包含匹配）
                return urlPath === noAuthUrl || urlPath.startsWith(noAuthUrl + '/') || url.includes(noAuthUrl)
            })
            
            // 如果接口需要token，且请求头中还没有Authorization，则添加token
            if (needsAuth && !config.headers.Authorization) {
                // 从localStorage获取token
                const token = localStorage.getItem('token')
                
                // 如果存在token，添加到请求头
                if (token && token.trim()) {
                    // 确保headers对象存在
                    if (!config.headers) {
                        config.headers = {}
                    }
                    config.headers.Authorization = `Bearer ${token.trim()}`
                }
            }
        } catch (e) {
            // 如果获取token失败，记录错误但不阻止请求
            console.warn('请求拦截器处理token失败:', e)
        }
        
        return config
    },
    error => {
        // 请求错误处理
        return Promise.reject(error)
    }
)

// 响应拦截器：处理响应和错误
service.interceptors.response.use(
    response => {
        return response.data
    },
    error => {
        // 处理错误响应
        if (error.response) {
            // 服务器返回了错误状态码
            const { status, data } = error.response;
            
            // 处理401未授权错误（token过期或无效）
            if (status === 401) {
                // 防止重复处理401错误
                if (!isHandling401) {
                    isHandling401 = true
                    
                    // 先获取当前路由路径，再清除token（避免清除后无法获取路由）
                    let currentPath = '/'
                    try {
                        // Vue 3中router.currentRoute是ref，需要使用.value
                        if (router && router.currentRoute && router.currentRoute.value) {
                            currentPath = router.currentRoute.value.path || window.location.hash.replace('#', '') || '/'
                        } else {
                            // 如果router未初始化，从window.location获取
                            currentPath = window.location.hash.replace('#', '') || '/'
                        }
                    } catch (e) {
                        // 如果获取路由失败，从window.location获取
                        console.warn('获取当前路由失败，使用window.location:', e)
                        try {
                            currentPath = window.location.hash.replace('#', '') || '/'
                        } catch (e2) {
                            currentPath = '/'
                        }
                    }
                    
                    // 清除本地存储的token和用户信息
                    try {
                        localStorage.removeItem('token')
                        localStorage.removeItem('user_name')
                        localStorage.removeItem('user_info')
                        localStorage.removeItem('login_result')
                    } catch (e) {
                        console.warn('清除localStorage失败:', e)
                    }
                    
                    // 触发auth-changed事件，通知其他组件更新状态
                    try {
                        window.dispatchEvent(new Event('auth-changed'))
                    } catch (e) {
                        console.warn('触发auth-changed事件失败:', e)
                    }
                    
                    // 如果当前不在登录页或注册页，则跳转到登录页
                    // 避免在登录页重复跳转导致循环
                    if (currentPath !== '/Login' && currentPath !== '/Register') {
                        // 使用setTimeout延迟执行跳转，避免在响应拦截器中直接跳转导致的问题
                        // 这样可以确保当前响应处理完成后再执行跳转
                        setTimeout(() => {
                            try {
                                if (router && typeof router.push === 'function') {
                                    router.push({
                                        path: '/Login',
                                        query: {
                                            redirect: currentPath // 保存当前路径，登录后可以跳转回来
                                        }
                                    }).catch(err => {
                                        // 忽略导航重复的错误
                                        if (err && err.name !== 'NavigationDuplicated') {
                                            console.error('路由跳转错误:', err)
                                        }
                                        // 如果router.push失败，使用window.location跳转作为备选方案
                                        try {
                                            window.location.href = '#/Login?redirect=' + encodeURIComponent(currentPath)
                                        } catch (e) {
                                            console.error('window.location跳转也失败:', e)
                                        }
                                    })
                                } else {
                                    // 如果router不可用，直接使用window.location跳转
                                    window.location.href = '#/Login?redirect=' + encodeURIComponent(currentPath)
                                }
                            } catch (e) {
                                // 如果router.push失败，使用window.location跳转
                                console.warn('router.push失败，使用window.location跳转:', e)
                                try {
                                    window.location.href = '#/Login?redirect=' + encodeURIComponent(currentPath)
                                } catch (e2) {
                                    console.error('所有跳转方式都失败:', e2)
                                }
                            }
                        }, 100) // 增加延迟时间，确保响应处理完成
                    }
                    
                    // 重置标记，延迟重置避免短时间内重复触发
                    setTimeout(() => {
                        isHandling401 = false
                    }, 2000) // 增加延迟时间
                }
                
                return Promise.reject(new Error('认证失败，请重新登录'));
            } else if (status === 400) {
                return Promise.reject(new Error(data?.message || '请求参数错误'));
            } else if (status === 500) {
                return Promise.reject(new Error('服务器内部错误'));
            } else {
                return Promise.reject(new Error(data?.message || `请求失败 (${status})`));
            }
        } else if (error.request) {
            // 请求已发出但没有收到响应
            return Promise.reject(new Error('网络连接失败，请检查网络'));
        } else {
            // 其他错误
            return Promise.reject(new Error(error.message || '请求失败'));
        }
    }
)

export default service
